import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Modal from './Modal';
import MemberRequirementsPopup from './MemberRequirementsPopup';
import NumericInput from './NumericInput';
import Button from './ui/Button';
import { ConfirmModal } from './ui';
import EventStatusBadge, { type EventStatus } from './EventStatusBadge';
import './AttendanceOverview.css';
import { translateError } from '../utils/errorMessages';

interface RequirementsStatus {
  total: number;
  completed: number;
  pending: number;
  missing: string[];
}

interface MemberAttendance {
  _id: string;
  name: string;
  color?: string;
  status?: 'attending' | 'not-attending' | 'maybe';
  note?: string;
  paymentStatus?: 'paid' | 'partial' | 'unpaid' | null;
  paidAmount?: number;
  requirementsStatus?: RequirementsStatus;
  /** D18: false = outside the viewer's scope, name only (missing = true). */
  canViewDetails?: boolean;
}

/**
 * Structured i18n hint returned by the backend coverage resolver. The
 * frontend renders via t(key, params) so we don't hardcode coverage strings.
 */
interface CoverageLabel {
  key: string;
  params?: Record<string, string | number>;
}

interface CoveragePreviewEntry {
  memberId: number;
  method: 'season' | 'calendar' | 'time' | 'entry' | 'needs_payment' | 'free';
  memberMembershipId: number | null;
  label?: CoverageLabel;
}

/**
 * Coverage methods that mean "the session is covered by a membership the member
 * already holds" — i.e. nothing is due for this event. These count as paid for
 * the payment badge; the specific membership is surfaced as secondary info
 * (tooltip / tap) rather than a separate always-visible chip.
 */
const MEMBERSHIP_COVERAGE_METHODS: ReadonlyArray<CoveragePreviewEntry['method']> = [
  'season', 'calendar', 'time', 'entry',
];

/** An applied group label (D15): roster members currently in it, not the whole group. */
interface GroupAttendance {
  _id: string;
  name: string;
  members: MemberAttendance[];
}

interface AttendanceData {
  eventId: string;
  eventName?: string;
  defaultAttendance: boolean | null;
  price?: number;
  groups: GroupAttendance[];
  directMembers: MemberAttendance[];
  waitingList: (MemberAttendance & { joinedAt: string })[];
}

interface AttendanceOverviewProps {
  eventId: string;
  eventName?: string;
  authToken?: string; // Kept for backward compatibility but no longer used
  /** Per-occurrence status (0056). A cancelled session refuses `attending:
   * true` server-side, so the marks are locked here rather than 409-ing. */
  eventStatus?: EventStatus;
}

const AttendanceOverview: React.FC<AttendanceOverviewProps> = ({ eventId, eventName, eventStatus }) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [data, setData] = useState<AttendanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attendanceLoading, setAttendanceLoading] = useState<string | null>(null);
  // memberId -> CoveragePreviewEntry. Updated on initial load and after each
  // attendance mark/unmark using the response's coverage / reverse fields.
  const [coverageByMember, setCoverageByMember] = useState<Map<number, CoveragePreviewEntry>>(new Map());
  // Which member's coverage detail is expanded inline (tap-to-reveal on mobile,
  // where the badge's hover title isn't available). Desktop uses the title.
  const [coverageInfoMember, setCoverageInfoMember] = useState<number | null>(null);

  // A cancelled session refuses `attending: true` server-side; locking the
  // control is honest about that instead of letting the click fail with a 409.
  const isCancelled = eventStatus === 'cancelled';
  const canEditAttendance = hasPermission('events:write') && !isCancelled;
  const canCancelCharge = hasPermission('payments:delete');
  // "Not attending" never cancels a charge by itself (members could dodge
  // cancellation fees). When the admin marks someone not attending and an
  // unpaid intent is open, ask whether to cancel it as a separate, explicit act.
  const [chargePrompt, setChargePrompt] = useState<{
    intentId: number; memberId: string; memberName: string; amount: number;
  } | null>(null);
  const [cancellingCharge, setCancellingCharge] = useState(false);

  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberAttendance | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState<string | null>(null);
  const [waitlistAction, setWaitlistAction] = useState<{ type: 'promote' | 'remove'; memberId: string; memberName: string } | null>(null);

  // Requirements popup state
  const [requirementsPopupOpen, setRequirementsPopupOpen] = useState(false);
  const [selectedMemberForRequirements, setSelectedMemberForRequirements] = useState<MemberAttendance | null>(null);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const [attRes, coverageRes] = await Promise.all([
        apiClient.get<AttendanceData>(`/api/events/${eventId}/attendance`),
        api.events.getCoveragePreview(eventId).catch(() => ({ data: { members: [] } })),
      ]);
      setData(attRes.data);
      const map = new Map<number, CoveragePreviewEntry>();
      for (const entry of coverageRes.data.members || []) {
        map.set(entry.memberId, entry as CoveragePreviewEntry);
      }
      setCoverageByMember(map);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const openPaymentModal = (member: MemberAttendance) => {
    setSelectedMember(member);
    // Prefill amount with remaining payment (price - already paid)
    const remaining = (data?.price || 0) - (member.paidAmount || 0);
    setPaymentAmount(remaining > 0 ? remaining.toString() : '');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentDescription('');
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedMember(null);
    setPaymentAmount('');
    setPaymentDescription('');
  };

  const openRequirementsPopup = (member: MemberAttendance) => {
    setSelectedMemberForRequirements(member);
    setRequirementsPopupOpen(true);
  };

  const closeRequirementsPopup = () => {
    setRequirementsPopupOpen(false);
    setSelectedMemberForRequirements(null);
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMember) return;

    setPaymentLoading(true);
    try {
      await apiClient.post(
        '/api/payments',
        {
          member: selectedMember._id,
          event: eventId,
          date: paymentDate,
          amount: parseFloat(paymentAmount),
          description: paymentDescription,
        }
      );
      closePaymentModal();
      // Refresh attendance data to update payment status
      await fetchAttendance();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleWaitlistActionConfirm = async () => {
    if (!waitlistAction) return;
    const { type, memberId } = waitlistAction;

    try {
      setWaitlistLoading(memberId);
      if (type === 'promote') {
        await apiClient.post(
          `/api/events/${eventId}/waitlist/${memberId}/promote`,
          {}
        );
      } else {
        await apiClient.delete(
          `/api/events/${eventId}/waitlist/${memberId}`
        );
      }
      await fetchAttendance();
    } catch (err: any) {
      alert(translateError(err, t));
    } finally {
      setWaitlistLoading(null);
      setWaitlistAction(null);
    }
  };

  const formatCzk = (n: number) =>
    `${new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)} Kč`;

  const handleCancelChargeConfirm = async () => {
    if (!chargePrompt) return;
    setCancellingCharge(true);
    try {
      await api.payments.deleteIntent(chargePrompt.intentId);
      addToast(t('attendance.cancelChargeSuccess'), 'success');
      await fetchAttendance();
    } catch (err: any) {
      addToast(translateError(err, t, 'attendance.updateError'), 'error');
    } finally {
      setCancellingCharge(false);
      setChargePrompt(null);
    }
  };

  const handleAdminAttendanceUpdate = async (
    memberId: string,
    currentStatus: 'attending' | 'not-attending' | 'maybe',
    memberName: string,
  ) => {
    // Cycle: attending -> not attending -> maybe -> attending
    let newAttending: boolean | null;
    let toastKey: string;
    if (currentStatus === 'attending') {
      newAttending = false;
      toastKey = 'attendance.markedNotAttending';
    } else if (currentStatus === 'not-attending') {
      newAttending = null;
      toastKey = 'attendance.markedMaybe';
    } else {
      newAttending = true;
      toastKey = 'attendance.markedAttending';
    }

    try {
      setAttendanceLoading(memberId);
      const resp = await api.events.updateAttendance(eventId, {
        memberId: Number(memberId),
        attending: newAttending,
      });
      addToast(t(toastKey), 'success');

      const respData = resp.data as {
        coverage?: { method: CoveragePreviewEntry['method']; memberMembershipId: number | null; label?: CoverageLabel } | null;
        reverse?: { method: string | null; paidIntentRequiresManualRefund: boolean; intentId: number | null } | null;
        openIntent?: { id: number; status: string; amount: number; paidAmount: number; balance: number } | null;
      };

      // Update local coverage map from the response so the row pill reflects
      // the actual outcome without re-fetching everything.
      if (respData.coverage) {
        const next = new Map(coverageByMember);
        next.set(Number(memberId), {
          memberId: Number(memberId),
          method: respData.coverage.method,
          memberMembershipId: respData.coverage.memberMembershipId,
          label: respData.coverage.label,
        });
        setCoverageByMember(next);
      } else if (respData.reverse) {
        // Reversal — drop the cached label; the next preview fetch will repopulate.
        const next = new Map(coverageByMember);
        next.delete(Number(memberId));
        setCoverageByMember(next);
        if (respData.reverse.paidIntentRequiresManualRefund) {
          addToast(t('attendance.paidAfterRefundWarning'), 'warning');
        }
      }

      // Explicit "not attending" with an unpaid charge still open: the admin
      // decides. (A reset to "maybe" never prompts — unknown ≠ declined.)
      const open = respData.openIntent;
      // The backend only reports cancellable intents (pending, nothing paid);
      // re-checked here so a partially paid charge can never be waived by a
      // click that was meant to record attendance.
      if (
        newAttending === false && open && open.status === 'pending'
        && open.paidAmount === 0 && open.balance > 0
      ) {
        const amount = formatCzk(open.balance);
        if (canCancelCharge) {
          setChargePrompt({ intentId: open.id, memberId, memberName, amount: open.balance });
        } else {
          addToast(t('attendance.openChargeRemains', { name: memberName, amount }), 'info');
        }
      }

      await fetchAttendance();
    } catch (err: any) {
      addToast(translateError(err, t, 'attendance.updateError'), 'error');
    } finally {
      setAttendanceLoading(null);
    }
  };

  const getStatusIcon = (status: 'attending' | 'not-attending' | 'maybe') => {
    switch (status) {
      case 'attending':
        return <span className="status-icon attending">&#10003;</span>;
      case 'not-attending':
        return <span className="status-icon not-attending">&#10007;</span>;
      case 'maybe':
        return <span className="status-icon maybe">?</span>;
    }
  };

  const getGroupStatus = (members: MemberAttendance[]): 'attending' | 'not-attending' | 'maybe' => {
    if (members.length === 0) return 'maybe';

    const hasMaybe = members.some(m => (m.status ?? 'maybe') === 'maybe');
    const allAttending = members.every(m => m.status === 'attending');
    const allNotAttending = members.every(m => m.status === 'not-attending');

    if (allAttending) return 'attending';
    if (allNotAttending) return 'not-attending';
    if (hasMaybe) return 'maybe';
    return 'maybe'; // Mixed states
  };

  const getRequirementsIndicator = (member: MemberAttendance) => {
    const reqStatus = member.requirementsStatus;
    if (!reqStatus || reqStatus.total === 0) return null;

    const { completed = 0, pending = 0, missing = [], total } = reqStatus;

    // Determine indicator color
    let indicatorClass = 'req-indicator';
    let title = '';

    if (completed === total) {
      indicatorClass += ' req-complete';
      title = t('requirements.allComplete');
    } else if (pending > 0) {
      indicatorClass += ' req-pending';
      title = t('requirements.somePending', { count: pending });
    } else if (missing.length > 0) {
      indicatorClass += ' req-missing';
      title = t('requirements.someMissing', { missing: missing.join(', ') });
    }

    return (
      <span className={indicatorClass} title={title}>
        {completed}/{total}
      </span>
    );
  };

  const getPaymentSection = (member: MemberAttendance) => {
    const cov = coverageByMember.get(Number(member._id));
    // Covered by a membership the member already holds → nothing due for this
    // event; treat it as paid on the badge.
    const coveredByMembership = !!cov && MEMBERSHIP_COVERAGE_METHODS.includes(cov.method);
    const hasPrice = !!data?.price && data.price > 0;

    // No per-event price AND no membership coverage to surface → no badge.
    if (!hasPrice && !coveredByMembership) return null;

    const isPaid = member.paymentStatus === 'paid' || coveredByMembership;
    const badgeClass = isPaid ? 'payment-paid' :
                       member.paymentStatus === 'partial' ? 'payment-partial' : 'payment-unpaid';
    const label = isPaid
      ? t('attendance.paid')
      : member.paymentStatus ? t(`attendance.${member.paymentStatus}`) : t('attendance.unpaid');

    // Which membership covers this member is secondary info — folded into a
    // hover tooltip on the Paid badge (tap to toggle inline on mobile) instead
    // of a separate always-visible chip. You don't need it at first glance.
    const coverageInfo = coveredByMembership && cov?.label
      ? t(cov.label.key, cov.label.params || {})
      : null;
    // For an uncovered, unpaid attendee the "X due" text from the resolver is
    // the badge/quick-pay tooltip (unchanged behaviour).
    const dueTooltip = !isPaid && cov?.method === 'needs_payment' && cov.label
      ? t(cov.label.key, cov.label.params || {})
      : null;
    const infoOpen = coverageInfoMember === Number(member._id);

    return (
      <div className="payment-section">
        <span
          className={`payment-badge ${badgeClass}${coverageInfo ? ' has-coverage-info' : ''}`}
          title={coverageInfo || dueTooltip || undefined}
          role={coverageInfo ? 'button' : undefined}
          tabIndex={coverageInfo ? 0 : undefined}
          onClick={coverageInfo ? (e) => {
            e.stopPropagation();
            setCoverageInfoMember((prev) => (prev === Number(member._id) ? null : Number(member._id)));
          } : undefined}
          onKeyDown={coverageInfo ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCoverageInfoMember((prev) => (prev === Number(member._id) ? null : Number(member._id)));
            }
          } : undefined}
          style={coverageInfo ? { cursor: 'pointer' } : undefined}
        >
          {label}
        </span>
        {coverageInfo && infoOpen && (
          <span
            className="coverage-info-reveal"
            style={{
              fontSize: '0.7rem',
              padding: '0.15rem 0.5rem',
              borderRadius: '999px',
              background: 'var(--bg-medium)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              whiteSpace: 'nowrap',
            }}
          >
            {coverageInfo}
          </span>
        )}
        {hasPrice && (
          <button
            type="button"
            className="add-payment-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              openPaymentModal(member);
            }}
            title={dueTooltip || t('payments.addPayment')}
          >
            $
          </button>
        )}
      </div>
    );
  };

  const renderMemberRow = (row: MemberAttendance) => {
    // Name-only rows (D18) may omit everything but name/status.
    const member = { ...row, status: row.status ?? 'maybe' } as MemberAttendance & { status: 'attending' | 'not-attending' | 'maybe' };
    const isUpdating = attendanceLoading === member._id;
    const nameOnly = member.canViewDetails === false;
    return (
      <div key={member._id} className={`attendance-member ${member.status}`}>
        <div className="member-left">
          {canEditAttendance ? (
            <button
              type="button"
              className={`status-icon-btn status-icon ${member.status}`}
              onClick={() => handleAdminAttendanceUpdate(member._id, member.status, member.name)}
              disabled={isUpdating}
              title={t('attendance.adminUpdate')}
            >
              {isUpdating ? (
                <span className="status-spinner">...</span>
              ) : member.status === 'attending' ? (
                <>&#10003;</>
              ) : member.status === 'not-attending' ? (
                <>&#10007;</>
              ) : (
                <>?</>
              )}
            </button>
          ) : (
            getStatusIcon(member.status)
          )}
          <div className="member-info">
            <div className="member-info-row">
              {nameOnly ? (
                <span className="member-name" title={t('attendance.detailsOutOfScope')}>{member.name}</span>
              ) : (
                <>
                  <button
                    type="button"
                    className="member-name-btn"
                    onClick={() => openRequirementsPopup(member)}
                    title={t('requirements.viewRequirements')}
                  >
                    {member.name}
                  </button>
                  {getRequirementsIndicator(member)}
                </>
              )}
            </div>
            {!nameOnly && member.note && (
              <div className="attendance-note-line">
                {member.note}
              </div>
            )}
          </div>
        </div>
        {!nameOnly && getPaymentSection(member)}
      </div>
    );
  };

  if (loading) {
    return <div className="attendance-overview loading">{t('attendance.loading')}</div>;
  }

  if (error) {
    return <div className="attendance-overview error">{t('common.error')}: {error}</div>;
  }

  if (!data) {
    return <div className="attendance-overview empty">{t('common.noResults')}</div>;
  }

  // A label whose members all left the roster has nothing to show.
  const labelGroups = data.groups.filter((g) => g.members.length > 0);
  const hasGroups = labelGroups.length > 0;
  const hasDirectMembers = data.directMembers.length > 0;

  if (!hasGroups && !hasDirectMembers) {
    return (
      <div className="attendance-overview empty">
        <p>{t('attendance.noMembersAssigned')}</p>
      </div>
    );
  }

  return (
    <div className="attendance-overview">
      <h4 className="attendance-title">{t('attendance.title')}</h4>
      {isCancelled && (
        <p className="attendance-cancelled-notice" role="status">
          <EventStatusBadge status="cancelled" />
          <span>{t('attendance.cancelledLocked')}</span>
        </p>
      )}
      <p className="attendance-default">
        {t('attendance.default')} <strong>{data.defaultAttendance === true ? t('common.yes') : data.defaultAttendance === false ? t('common.no') : t('common.maybe')}</strong>
      </p>

      {hasGroups && (
        <div className="attendance-groups">
          {labelGroups.map((group) => {
            const groupStatus = getGroupStatus(group.members);
            return (
              <div key={group._id} className={`attendance-group ${groupStatus}`}>
                <div className="group-header">
                  {getStatusIcon(groupStatus)}
                  <span className="group-name">{group.name}</span>
                  <span className="group-count">({group.members.length})</span>
                </div>
                <div className="group-members">
                  {group.members.map(renderMemberRow)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasDirectMembers && (
        <div className="attendance-direct">
          <div className="direct-header">{t('attendance.directMembers')}</div>
          <div className="direct-members">
            {data.directMembers.map(renderMemberRow)}
          </div>
        </div>
      )}

      {data.waitingList && data.waitingList.length > 0 && (
        <div className="attendance-waiting-list" style={{ marginTop: '2rem' }}>
          <div className="direct-header" style={{ color: 'var(--warning-dark)', borderColor: 'var(--warning-light)' }}>
            Waitlist ({data.waitingList.length})
          </div>
          <div className="direct-members">
            {data.waitingList.map((member) => (
              <div key={member._id} className="attendance-member maybe" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="member-left">
                  <span className="status-icon maybe">&#8987;</span>
                  <span className="member-name">{member.name}</span>
                  <small style={{ marginLeft: '1rem', color: 'var(--text-muted)' }}>
                    Joined: {new Date(member.joinedAt).toLocaleString()}
                  </small>
                </div>
                <div className="waitlist-actions" style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    size="sm"
                    style={{ background: 'var(--success-color)' }}
                    onClick={() => setWaitlistAction({ type: 'promote', memberId: member._id, memberName: member.name })}
                    disabled={waitlistLoading === member._id}
                  >
                    {waitlistLoading === member._id ? '...' : 'Add to Attendees'}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setWaitlistAction({ type: 'remove', memberId: member._id, memberName: member.name })}
                    disabled={waitlistLoading === member._id}
                  >
                    {waitlistLoading === member._id ? '...' : 'Remove'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <Modal
        isOpen={paymentModalOpen}
        onClose={closePaymentModal}
        title={t('payments.addPayment')}
        size="small"
      >
        {selectedMember && (
          <form onSubmit={handleAddPayment} className="payment-modal-form">
            <div className="payment-modal-member">
              <strong>{selectedMember.name}</strong>
            </div>

            <div className="form-group">
              <label htmlFor="modal-payment-amount">{t('payments.amount')}</label>
              <NumericInput
                id="modal-payment-amount"
                step="1"
                min="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="modal-payment-date">{t('payments.date')}</label>
              <input
                type="date"
                id="modal-payment-date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="modal-payment-description">{t('payments.description')}</label>
              <input
                type="text"
                id="modal-payment-description"
                value={paymentDescription}
                onChange={(e) => setPaymentDescription(e.target.value)}
              />
            </div>

            <div className="payment-modal-actions">
              <Button type="submit" fullWidth loading={paymentLoading}>
                {paymentLoading ? t('common.saving') : t('payments.addButton')}
              </Button>
              <Button variant="secondary" fullWidth onClick={closePaymentModal}>
                {t('payments.cancelButton')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Member Requirements Popup */}
      {selectedMemberForRequirements && (
        <MemberRequirementsPopup
          isOpen={requirementsPopupOpen}
          onClose={closeRequirementsPopup}
          memberId={selectedMemberForRequirements._id}
          memberName={selectedMemberForRequirements.name}
          eventId={eventId}
          eventName={eventName}
          onUpdate={fetchAttendance}
        />
      )}

      <ConfirmModal
        isOpen={!!waitlistAction}
        onClose={() => setWaitlistAction(null)}
        onConfirm={handleWaitlistActionConfirm}
        title={waitlistAction?.type === 'remove'
          ? t('attendance.removeFromWaitlistTitle')
          : t('attendance.promoteFromWaitlistTitle')}
        message={waitlistAction
          ? t(
              waitlistAction.type === 'remove'
                ? 'attendance.removeFromWaitlistConfirm'
                : 'attendance.promoteFromWaitlistConfirm',
              { name: waitlistAction.memberName },
            )
          : ''}
        confirmText={waitlistAction?.type === 'remove' ? t('common.remove') : t('common.add')}
        cancelText={t('common.cancel')}
        variant={waitlistAction?.type === 'remove' ? 'danger' : 'default'}
        loading={!!waitlistLoading}
      />

      <ConfirmModal
        isOpen={!!chargePrompt}
        onClose={() => setChargePrompt(null)}
        onConfirm={handleCancelChargeConfirm}
        title={t('attendance.cancelChargeTitle')}
        message={chargePrompt
          ? t('attendance.cancelChargeConfirm', {
              name: chargePrompt.memberName,
              amount: formatCzk(chargePrompt.amount),
            })
          : ''}
        confirmText={t('payments.deleteIntent')}
        cancelText={t('attendance.keepCharge')}
        variant="danger"
        loading={cancellingCharge}
      />
    </div>
  );
};

export default AttendanceOverview;
